import { useUser } from '@clerk/react';
import { useEffect, useRef } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useSequenceOwners } from '../use-sequence-owners.hook';
import { ChecksView } from './connect-mailbox-checks.component';
import {
  AlreadyView,
  ChooseView,
  RefusedView,
  TroubleView,
  WaitingView,
} from './connect-mailbox-views.component';
import type { ConnectProvider } from './connect-mailbox.util';
import { useConnectMailbox } from './use-connect-mailbox.hook';

/**
 * Connect a mailbox without leaving the page: pick the provider, sign in in a
 * window (the dialog waits on the page behind it), then check the address, its
 * domain records and its daily limit. The same dialog opens from the Launch
 * blocker, Settings and the empty states.
 *
 * `returnLabel` names where the person came from ("Tax directors Q4"), and
 * the last step's button returns there; without it the button says Done.
 *
 * `initialProvider` is for a button that already named the provider: opening
 * the dialog starts that provider's sign-in at once instead of asking again.
 * It starts in the render the click caused (React flushes the effects of a
 * click before the browser paints), so the sign-in window opens inside the
 * click's gesture and is not blocked.
 */
export function ConnectMailboxDialog({
  open,
  onOpenChange,
  returnLabel,
  initialProvider,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  returnLabel?: string;
  initialProvider?: ConnectProvider | null;
  /** Called when the person finishes the checks, after the dialog closes. */
  onDone?: () => void;
}) {
  const flow = useConnectMailbox({ open });
  const { pick } = flow;
  const autoStarted = useRef(false);
  useEffect(() => {
    if (!open) {
      autoStarted.current = false;
      return;
    }
    if (initialProvider && !autoStarted.current) {
      autoStarted.current = true;
      pick(initialProvider);
    }
  }, [open, initialProvider, pick]);
  const { user } = useUser();
  const resolveOwner = useSequenceOwners();
  const { phase } = flow;
  const close = () => onOpenChange(false);
  // The mailbox a finished sign-in names, once the list has it.
  const known = flow.mailboxes.find(
    (item) =>
      (phase.kind === 'checks' || phase.kind === 'already') &&
      item.id === phase.mailboxId
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-md gap-0 overflow-hidden p-0'>
        {phase.kind === 'choose' && (
          <ChooseView onPick={flow.pick} onCancel={close} />
        )}
        {(phase.kind === 'starting' || phase.kind === 'waiting') && (
          <WaitingView phase={phase} onReopen={flow.reopen} onCancel={close} />
        )}
        {(phase.kind === 'closed' ||
          phase.kind === 'slow' ||
          phase.kind === 'failed') && (
          <TroubleView
            phase={phase}
            onCancel={close}
            onTryAgain={flow.pick}
            onCheckAgain={flow.recheck}
          />
        )}
        {phase.kind === 'checks' && (
          <ChecksView
            mailbox={known}
            doneLabel={returnLabel ? 'Back to ' + returnLabel : 'Done'}
            onDone={() => {
              close();
              onDone?.();
            }}
          />
        )}
        {phase.kind === 'already' && (
          <AlreadyView
            email={phase.email}
            provider={known?.provider ?? 'google_workspace'}
            ownerName={
              phase.ownerId === user?.id
                ? 'you'
                : resolveOwner(phase.ownerId).name
            }
            shared={known?.shared ?? false}
            onClose={close}
            onUseAnother={flow.restart}
          />
        )}
        {phase.kind === 'refused' && (
          <RefusedView message={phase.message} onClose={close} />
        )}
      </DialogContent>
    </Dialog>
  );
}
